import { T } from "./Translate";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Container,
  AppBar,
  Toolbar,
  ListItem,
  List,
  Avatar,
  Skeleton,
  Button,
  Stack,
} from "@mui/material";
import BackButton from "./BackButton";
import { auth, db } from "../firebase";
import BottomToolbar from "./BottomToolbar";
import { respondFollowRequest } from "../utils/follow";
import { Notifications as NotificationsIcon } from '../icons';
import { Email as EmailIcon } from '../icons';
import { Event as EventIcon } from '../icons';
import { LocalOffer as LocalOfferIcon } from '../icons';
import { PersonAddAlt1 as PersonAddAlt1Icon } from '../icons';
import { DeliveryDining as DeliveryDiningIcon } from "../icons";
import { Storefront as StorefrontIcon } from '../icons';
import { TimerOutlined as TimerIcon } from '../icons';
import { NotificationsActive as NotificationsActiveIcon } from '../icons';
import { SupportAgent as SupportAgentIcon } from '../icons';
import { brandColors, barColors } from "../theme";

// "Seguir publicación" (functions/itemFollows.js): ficha de cada tipo.
const ITEM_PATHS = { offer: "/offers/", event: "/events/", car: "/cars/", place: "/trips/" };

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      console.error("No user is currently authenticated");
      setLoading(false);
      return;
    }

    const unsubscribe = db
      .collection("notifications")
      .where("userId", "==", user.uid)
      .orderBy("timestamp", "desc")
      .onSnapshot((snapshot) => {
        const notificationsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setNotifications(notificationsData);
        setLoading(false);

        // Marcar todas las notificaciones como leídas
        snapshot.docs.forEach((doc) => {
          if (!doc.data().read) {
            doc.ref.update({ read: true });
          }
        });
      });

    return () => unsubscribe();
  }, []);

const formatDate = (timestamp) => {
    if (timestamp && timestamp.toDate) {
      const date = timestamp.toDate();
      return date.toLocaleDateString('es-ES', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return "";
  };

  const handleNotificationClick = (notification) => {
    if (notification.type === "message") {
      navigate("/chats");
    } else if (notification.type === "event_invite") {
      navigate("/events");
    } else if (
      notification.type === "offer" ||
      notification.type === "event" ||
      notification.type === "follower" ||
      notification.type === "follow_request" ||
      notification.type === "follow_accepted"
    ) {
      navigate(`/profile/${notification.companyId}`);
    } else if (notification.type === "store_created") {
      navigate("/my-stores");
    } else if (notification.type === "nearby_event") {
      navigate(`/events/${notification.postId}`);
    } else if (notification.type === "nearby_store") {
      navigate(`/stores/${notification.postId}`);
    } else if (notification.type === "food_order" || notification.type === "restaurant_plan") {
      navigate("/pedidos/mi-local");
    } else if (notification.type === "food_order_status") {
      navigate(`/pedidos/orden/${notification.postId}`);
    } else if (notification.type === "publication_limit") {
      navigate("/subscription");
    } else if (notification.type === "support_reply") {
      navigate("/support/chat");
    } else if (
      (notification.type === "item_reminder" || notification.type === "item_update") &&
      ITEM_PATHS[notification.itemType]
    ) {
      navigate(`${ITEM_PATHS[notification.itemType]}${notification.postId}`);
    }
  };

  // Aceptar/rechazar una solicitud de seguimiento (cuenta privada, ver
  // ajustes de privacidad y utils/follow.js). Se marca "handled" en la
  // propia notificación para no reprocesar la misma solicitud si el usuario
  // vuelve a esta pantalla más tarde.
  const handleRespondRequest = async (event, notification, accept) => {
    event.stopPropagation();
    const user = auth.currentUser;
    if (!user) return;
    try {
      await respondFollowRequest({
        targetUid: user.uid,
        targetDisplayName: user.displayName,
        requesterUid: notification.companyId,
        accept,
      });
      await db.collection("notifications").doc(notification.id).update({ handled: true, accepted: accept });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, handled: true, accepted: accept } : n))
      );
    } catch (error) {
      console.error("Error responding to follow request:", error);
    }
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case "message":
        return <EmailIcon sx={styles.icon} />;
      case "offer":
        return <LocalOfferIcon sx={styles.icon} />;
      case "event":
      case "event_invite":
      case "nearby_event":
        return <EventIcon sx={styles.icon} />;
      case "follower":
      case "follow_request":
      case "follow_accepted":
        return <PersonAddAlt1Icon sx={styles.icon} />;
      case "store_created":
      case "nearby_store":
        return <StorefrontIcon sx={styles.icon} />;
      case "food_order":
      case "food_order_status":
      case "restaurant_plan":
        return <DeliveryDiningIcon sx={styles.icon} />;
      case "item_reminder":
        return <TimerIcon sx={styles.icon} />;
      case "item_update":
        return <NotificationsActiveIcon sx={styles.icon} />;
      case "support_reply":
        return <SupportAgentIcon sx={styles.icon} />;
      default:
        return <NotificationsIcon sx={styles.icon} />;
    }
  };

  const getNotificationColor = (type) => {
    switch(type) {
      case "message":
        return "#4CAF50";
      case "offer":
        return "#2196F3";
      case "event":
        return "#FF9800";
      case "event_invite":
        return "#9C27B0";
      case "follower":
      case "follow_accepted":
        return "#00BCD4";
      case "follow_request":
        return "#E91E63";
      case "store_created":
        return "#3F51B5";
      case "nearby_event":
        return "#8E24AA";
      case "nearby_store":
        return "#E65100";
      case "food_order":
      case "food_order_status":
      case "restaurant_plan":
        return "#D32F2F";
      case "item_reminder":
        return "#F57C00";
      case "item_update":
        return "#00897B";
      case "support_reply":
        return "#27ae60";
      default:
        return "#9E9E9E";
    }
  };

  const renderNotificationItem = (notification) => {
    const notificationColor = getNotificationColor(notification.type);
    return (
      <ListItem 
        key={notification.id} 
        sx={{
          ...styles.listItem,
          borderLeft: `4px solid ${notificationColor}`,
        }}
        onClick={() => handleNotificationClick(notification)}
      >
        <Box sx={styles.avatarContainer}>
          <Avatar sx={{ bgcolor: notificationColor }}>
            {getNotificationIcon(notification.type)}
          </Avatar>
        </Box>
        <Box sx={styles.contentContainer}>
          <Typography variant="subtitle1" sx={styles.notificationType}>
            {notification.type === "message" ? <T>Nuevo mensaje</T> :
             notification.type === "offer" ? <T>Nueva oferta</T> :
             notification.type === "event" ? <T>Nuevo evento</T> :
             notification.type === "event_invite" ? <T>Invitación a evento</T> :
             notification.type === "follower" ? <T>Nuevo seguidor</T> :
             notification.type === "follow_request" ? <T>Solicitud para seguirte</T> :
             notification.type === "follow_accepted" ? <T>Solicitud aceptada</T> :
             notification.type === "store_created" ? <T>Tienda creada</T> :
             notification.type === "nearby_event" ? <T>Nuevo evento cerca tuyo</T> :
             notification.type === "nearby_store" ? <T>Nueva tienda cerca tuyo</T> :
             notification.type === "publication_limit" ? <T>Límite de publicaciones</T> :
             notification.type === "food_order" ? <T>Nuevo pedido</T> :
             notification.type === "food_order_status" ? <T>Tu pedido</T> :
             notification.type === "restaurant_plan" ? <T>Tu local en Pedidos</T> :
             notification.type === "item_reminder" ? <T>¡Falta poco!</T> :
             notification.type === "item_update" ? <T>Novedades de lo que seguís</T> :
             notification.type === "support_reply" ? <T>Respuesta de soporte</T> : <T>Notificación</T>}
          </Typography>
          <Typography variant="body2" sx={styles.notificationMessage}>
            {notification.message}
          </Typography>
          {notification.type === "follow_request" && (
            notification.handled ? (
              <Typography variant="caption" color={notification.accepted ? "success.main" : "text.secondary"}>
                {notification.accepted ? <T>Aceptaste esta solicitud</T> : <T>Rechazaste esta solicitud</T>}
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
                <Button size="small" variant="contained" onClick={(e) => handleRespondRequest(e, notification, true)}>
                  <T>Aceptar</T>
                </Button>
                <Button size="small" variant="outlined" onClick={(e) => handleRespondRequest(e, notification, false)}>
                  <T>Rechazar</T>
                </Button>
              </Stack>
            )
          )}
          <Typography variant="caption" sx={styles.notificationTimestamp}>
            {formatDate(notification.timestamp)}
          </Typography>
        </Box>
      </ListItem>
    );
  };

  const styles = {
    appBar: {
      bgcolor: barColors.background,
      color: barColors.text,
    },
    title: {
      ml: 1,
      fontWeight: "bold",
    },
    container: {
      mt: 10,
      mb: 10,
      px: 2,
      maxWidth: { xs: "100%", md: "980px" },
    },
    listItem: {
      display: "flex",
      alignItems: "flex-start",
      padding: 2,
      mb: 2,
      borderRadius: 2,
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      transition: "all 0.3s ease",
      cursor: "pointer",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
      },
    },
    avatarContainer: {
      mr: 2,
    },
    contentContainer: {
      flexGrow: 1,
    },
    notificationType: {
      fontWeight: "bold",
      color: brandColors.accent,
    },
    notificationMessage: {
      color: "text.secondary",
      mb: 1,
    },
    notificationTimestamp: {
      color: "text.disabled",
    },
    icon: {
      color: "white",
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "50vh",
    },
    noNotificationsText: {
      textAlign: "center",
      mt: 4,
      color: "text.secondary",
    },
  };

  return (
    <Box>
      <AppBar position="fixed" sx={styles.appBar}>
        <Toolbar sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", maxWidth: { xs: "100%", md: "980px" }, mx: "auto" }}>
            <BackButton color="inherit" />
            <Typography variant="h6" sx={styles.title}>
              <T>Notificaciones</T>
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Container sx={styles.container}>
        {loading ? (
          <List>
            {[1, 2, 3, 4, 5].map((i) => (
              <ListItem key={i} sx={{ mb: 2 }}>
                <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="80%" height={16} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="30%" height={14} />
                </Box>
              </ListItem>
            ))}
          </List>
        ) : notifications.length > 0 ? (
          <List>
            {notifications.map(renderNotificationItem)}
          </List>
        ) : (
          <Typography variant="body1" sx={styles.noNotificationsText}>
            <T>No tienes notificaciones.</T>
          </Typography>
        )}
      </Container>
      <BottomToolbar />
    </Box>
  );
};

export default Notifications;